import { useEffect, useMemo, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard, ShoppingBag, WalletCards, ReceiptText, Users, LogOut, Plus, RefreshCw,
  ShieldCheck, ArrowUpRight, Activity, Bell, Search, Circle, Menu, X,
  ChevronRight, TrendingUp, Wallet as WalletIcon, Zap, ArrowLeft, User as UserIcon, Trash2, FileText, LifeBuoy, CheckCheck,
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
const money = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
const nfmt = (n) => new Intl.NumberFormat("id-ID").format(n || 0);
const errorText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d || "Terjadi kesalahan. Coba lagi.";
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const STATUS_META = {
  Pending: { label: "Menunggu", cls: "processing" },
  Processing: { label: "Diproses", cls: "processing" },
  Success: { label: "Selesai", cls: "available" },
  Partial: { label: "Sebagian", cls: "warning" },
  Error: { label: "Gagal", cls: "danger" },
};

const DEFAULT_LANDING = { enabled: true, hero_title: "Jalankan order tanpa ragu.", hero_subtitle: "Wallet, layanan, dan status order dalam satu ruang kerja yang tenang." };

const TICKET_META = {
  open: { label: "Menunggu", cls: "processing" },
  in_progress: { label: "Diproses", cls: "warning" },
  resolved: { label: "Selesai", cls: "available" },
  closed: { label: "Ditutup", cls: "muted" },
};
const TICKET_CATEGORIES = ["Pesanan", "Refill", "Deposit", "Pembayaran", "Lainnya"];

// -------------------- PUBLIC: LANDING --------------------
function Landing({ pub }) {
  const [cfg, setCfg] = useState(pub || {});
  useEffect(() => { api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const landing = { ...DEFAULT_LANDING, ...(cfg.landing || {}) };
  return (
    <main className="auth-page">
      <div className="auth-mark">GG<span>°</span></div>
      <section className="auth-copy">
        <p className="eyebrow">GUNTAGO / DIGITAL SERVICES PANEL</p>
        <h1>{landing.hero_title}</h1>
        <p>{landing.hero_subtitle}</p>
        <div className="auth-note"><ShieldCheck size={18} aria-hidden /> Provider terisolasi · Pembayaran terverifikasi</div>
        <ul className="auth-list">
          <li><Circle size={7} fill="currentColor" /> Katalog Instagram, TikTok, YouTube, X, FB, Telegram</li>
          <li><Circle size={7} fill="currentColor" /> Wallet ledger dengan pencatatan setiap transaksi</li>
          <li><Circle size={7} fill="currentColor" /> Auto-sync status pesanan setiap 30 detik</li>
        </ul>
        <div className="landing-links">
          <Link to="/status" className="quiet-link"><Activity size={14} aria-hidden /> Status sistem</Link>
          <Link to="/terms" className="quiet-link">Syarat & Ketentuan</Link>
        </div>
      </section>
      <section className="auth-form landing-cta">
        <p className="eyebrow">MULAI SEKARANG</p>
        <h2>Masuk ke Guntago</h2>
        <p className="subtle">Kelola wallet, pesan layanan, dan pantau status order dari satu panel.</p>
        <Link to="/auth" data-testid="landing-cta-button" className="button primary wide">Masuk / Daftar <ArrowUpRight size={17} aria-hidden /></Link>
        <div className="landing-legal">
          <Link to="/terms">Syarat & Ketentuan</Link>
          <Link to="/privacy">Kebijakan Privasi</Link>
          <Link to="/status">Status Sistem</Link>
        </div>
      </section>
    </main>
  );
}

// -------------------- PUBLIC: CONTENT PAGE (terms / privacy) --------------------
function ContentPage({ slug }) {
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/public/pages/${slug}`).then((r) => setPage(r.data)).catch((x) => setErr(errorText(x))); }, [slug]);
  if (err) return (
    <main className="content-page-wrap">
      <section className="panel content-page">
        <p className="eyebrow">GUNTAGO / HALAMAN</p>
        <h1>Halaman tidak ditemukan.</h1>
        <p className="subtle">{err}</p>
        <Link to="/" className="button outline mt"><ArrowLeft size={15} aria-hidden /> Kembali ke beranda</Link>
      </section>
    </main>
  );
  if (!page) return <div className="loading">Memuat halaman...</div>;
  return (
    <main className="content-page-wrap">
      <section className="panel content-page">
        <p className="eyebrow">GUNTAGO / {page.slug.toUpperCase()}</p>
        <h1>{page.title}</h1>
        <div className="content-body">{page.content}</div>
        <Link to="/" className="button outline mt"><ArrowLeft size={15} aria-hidden /> Kembali ke beranda</Link>
      </section>
    </main>
  );
}

// -------------------- PUBLIC: SYSTEM STATUS --------------------
function StatusPage() {
  const [page, setPage] = useState(null);
  const [st, setSt] = useState(null);
  const load = () => {
    api.get("/public/pages/status").then((r) => setPage(r.data)).catch(() => {});
    api.get("/public/status").then((r) => setSt(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const operational = st?.status === "operational";
  return (
    <main className="content-page-wrap">
      <section className="panel content-page">
        <p className="eyebrow">GUNTAGO / STATUS SISTEM</p>
        <h1>{page?.title || "Status Sistem"}</h1>
        <div className={`status-hero ${st ? (operational ? "ok" : "warn") : ""}`}>
          {st && <span className="pulse" />}
          <div>
            <b>{st ? (operational ? "Semua sistem operasional" : "Ada gangguan") : "Memeriksa..."}</b>
            <small>{st ? `Diperiksa ${fmtDate(st.checked_at)}` : "Menghubungi server..."}</small>
          </div>
        </div>
        {st && (
          <div className="status-checks">
            {st.checks.map((c) => (
              <div className="status-check" key={c.name}>
                <span className={`status ${c.ok ? "available" : "danger"}`}>{c.ok ? "Operasional" : "Gangguan"}</span>
                <b>{c.name}</b>
                <small>{c.detail}</small>
              </div>
            ))}
          </div>
        )}
        <div className="content-body">{page?.content}</div>
        <div className="row-actions mt">
          <button className="button outline" onClick={load} data-testid="status-refresh-button"><RefreshCw size={15} aria-hidden /> Periksa ulang</button>
          <Link to="/" className="button outline"><ArrowLeft size={15} aria-hidden /> Kembali ke beranda</Link>
        </div>
      </section>
    </main>
  );
}

// -------------------- AUTH --------------------
function Auth({ onLogin, landingEnabled }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const r = await api.post(`/auth/${mode}`, form);
      onLogin(r.data);
    } catch (x) {
      setError(errorText(x));
    } finally { setBusy(false); }
  };

  return (
    <main className="auth-page">
      <div className="auth-mark">GG<span>°</span></div>
      <section className="auth-copy">
        <p className="eyebrow">GUNTAGO / DIGITAL SERVICES PANEL</p>
        <h1>Jalankan order<br /><em>tanpa ragu.</em></h1>
        <p>Wallet, layanan, dan status order dalam satu ruang kerja yang tenang.</p>
        <div className="auth-note"><ShieldCheck size={18} aria-hidden /> Provider terisolasi. Pembayaran auto verifikasi.</div>
        <ul className="auth-list">
          <li><Circle size={7} fill="currentColor" /> Katalog Instagram, TikTok, YouTube, X, FB, Telegram</li>
          <li><Circle size={7} fill="currentColor" /> Wallet ledger dengan pencatatan setiap transaksi</li>
          <li><Circle size={7} fill="currentColor" /> Auto-sync status pesanan setiap 30 detik</li>
        </ul>
      </section>
      <form className="auth-form" onSubmit={submit}>
        <div>
          <p className="eyebrow">{mode === "login" ? "WELCOME BACK" : "BUAT AKUN"}</p>
          <h2>{mode === "login" ? "Masuk ke Guntago" : "Mulai dengan Guntago"}</h2>
        </div>
        {mode === "register" && (
          <input data-testid="register-name-input" placeholder="Nama lengkap" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        )}
        <input data-testid="auth-email-input" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="auth-password-input" type="password" required placeholder="Password (min. 6 karakter)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        {error && <div data-testid="auth-error-message" className="error">{error}</div>}
        <button data-testid="auth-submit-button" disabled={busy} className="button primary">
          {busy ? "Memproses..." : mode === "login" ? "Masuk" : "Daftar akun"}<ArrowUpRight size={17} aria-hidden />
        </button>
        <button data-testid="auth-mode-toggle" type="button" className="text-button" onClick={() => setMode(mode === "login" ? "register" : "login")}>
          {mode === "login" ? "Belum punya akun? Daftar" : "Sudah punya akun? Masuk"}
        </button>
        {landingEnabled && <Link to="/" className="text-button">← Kembali ke beranda</Link>}
      </form>
    </main>
  );
}

// -------------------- SHELL --------------------
const NAV_ITEMS = [
  ["/dashboard", "Ringkasan", LayoutDashboard],
  ["/services", "Layanan", ShoppingBag],
  ["/tatacara-pemesanan", "Tatacara Pemesanan", FileText],
  ["/orders", "Pesanan", ReceiptText],
  ["/refills", "Refill", RefreshCw],
  ["/tickets", "Aduan", LifeBuoy],
  ["/wallet", "Wallet", WalletCards],
  ["/notifications", "Notifikasi", Bell],
  ["/profile", "Profil", UserIcon],
];
const ADMIN_NAV = [
  ["/admin", "Overview", LayoutDashboard],
  ["/admin/users", "Pengguna", Users],
  ["/admin/services", "Layanan", ShoppingBag],
  ["/admin/categories", "Kategori", Circle],
  ["/admin/pages", "Halaman", FileText],
  ["/admin/orders", "Pesanan", ReceiptText],
  ["/admin/tickets", "Aduan", LifeBuoy],
  ["/admin/deposits", "Deposit", WalletCards],
  ["/admin/settings", "Pengaturan", Zap],
];

function SidebarContent({ user, onLogout, unread, onNav }) {
  return (
    <>
      <div className="brand">GG<span>°</span><small> GUNTAGO</small></div>
      <div className="sidebar-profile">
        <div className="avatar">{user.name[0]?.toUpperCase()}</div>
        <div className="sidebar-profile-body">
          <strong>{user.name}</strong>
          <small>{user.role === "admin" ? "Administrator" : "Member"} · {user.email}</small>
        </div>
        <button data-testid="logout-button" className="logout-inline" onClick={onLogout} aria-label="Keluar akun">
          <LogOut size={15} aria-hidden /><span>Keluar</span>
        </button>
      </div>
      <p className="eyebrow nav-label">WORKSPACE</p>
      <nav>
        {NAV_ITEMS.map(([path, label, Icon]) => (
          <NavLink data-testid={`nav-${label.toLowerCase()}`} key={path} to={path} end={path === "/dashboard"} onClick={onNav} className={({ isActive }) => (isActive ? "active" : "")}>
            <Icon size={17} aria-hidden /><span className="nav-label-text">{label}</span>
            {label === "Notifikasi" && unread > 0 && <span className="nav-badge">{unread}</span>}
          </NavLink>
        ))}
      </nav>
      {user.role === "admin" && (
        <>
          <p className="eyebrow nav-label">ADMIN</p>
          <nav>
            {ADMIN_NAV.map(([path, label, Icon]) => (
              <NavLink data-testid={`nav-admin-${label.toLowerCase()}`} key={path} to={path} end={path === "/admin"} onClick={onNav} className={({ isActive }) => (isActive ? "active" : "")}>
                <Icon size={17} aria-hidden /><span className="nav-label-text">{label}</span>
              </NavLink>
            ))}
          </nav>
        </>
      )}
      <div className="sidebar-bottom">
        <button data-testid="logout-button-bottom" className="logout" onClick={onLogout}><LogOut size={16} aria-hidden /><span className="nav-label-text">Keluar dari Guntago</span></button>
      </div>
    </>
  );
}

function Shell({ user, onLogout, unread, children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  return (
    <div className="app-shell">
      <header className="mobile-header">
        <button data-testid="mobile-menu-open" className="icon-btn" onClick={() => setOpen(true)} aria-label="Buka menu"><Menu size={20} aria-hidden /></button>
        <div className="brand mobile-brand">GG<span>°</span></div>
        <div className="mobile-header-actions">
          <NavLink to="/notifications" className="icon-btn" aria-label="Notifikasi">
            <Bell size={18} aria-hidden />
            {unread > 0 && <span className="header-badge">{unread}</span>}
          </NavLink>
          <button data-testid="mobile-logout-button" className="icon-btn logout-btn" onClick={onLogout} aria-label="Keluar akun"><LogOut size={18} aria-hidden /></button>
        </div>
      </header>
      <aside className="sidebar-desktop"><SidebarContent user={user} onLogout={onLogout} unread={unread} /></aside>
      {open && (
        <>
          <div className="sheet-scrim" onClick={() => setOpen(false)} data-testid="mobile-menu-scrim" />
          <aside className="sidebar-mobile" data-testid="mobile-menu">
            <button className="sheet-close" onClick={() => setOpen(false)} aria-label="Tutup menu" data-testid="mobile-menu-close"><X size={18} aria-hidden /></button>
            <SidebarContent user={user} onLogout={() => { setOpen(false); onLogout(); }} unread={unread} onNav={() => setOpen(false)} />
          </aside>
        </>
      )}
      <main className="content">{children}</main>
    </div>
  );
}

const Metric = ({ label, value, detail, accent }) => (
  <div data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`} className={`metric ${accent || ""}`}>
    <span className="eyebrow">{label}</span>
    <strong>{value}</strong>
    <small>{detail}</small>
  </div>
);

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "processing" };
  return <span className={`status ${m.cls}`}>{m.label}</span>;
};

// -------------------- DASHBOARD --------------------
function Dashboard({ user }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="loading">Memuat workspace...</div>;
  const today = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).toUpperCase();
  return (
    <>
      <header className="page-head">
        <div>
          <p className="eyebrow">{today} / OVERVIEW</p>
          <h1>Halo, {user.name.split(" ")[0]}.</h1>
          <p className="subtle">Satu tempat untuk mengatur semua aktivitas digitalmu.</p>
        </div>
        <NavLink data-testid="dashboard-new-order-button" to="/services" className="button primary"><Plus size={18} aria-hidden /> Pesan layanan</NavLink>
      </header>
      <section className="hero-strip">
        <div className="hero-balance">
          <p className="eyebrow">SALDO TERSEDIA</p>
          <strong data-testid="dashboard-balance">{money(data.wallet.balance)}</strong>
          <div className="hero-actions">
            <NavLink data-testid="dashboard-topup-button" to="/wallet" className="button primary"><Plus size={15} aria-hidden /> Top-up</NavLink>
            <NavLink to="/services" className="button outline outline-light"><ShoppingBag size={15} aria-hidden /> Katalog</NavLink>
          </div>
        </div>
        <div className="hero-side">
          <div className="hero-tile"><span className="eyebrow">TOTAL ORDER</span><strong>{nfmt(data.metrics.total)}</strong></div>
          <div className="hero-tile"><span className="eyebrow">DIPROSES</span><strong>{nfmt(data.metrics.active)}</strong></div>
          <div className="hero-tile"><span className="eyebrow">SELESAI</span><strong>{nfmt(data.metrics.done)}</strong></div>
          <div className="hero-tile"><span className="eyebrow">NOTIFIKASI</span><strong>{nfmt(data.metrics.unread)}</strong></div>
        </div>
      </section>
      <section className="quick-actions">
        <p className="eyebrow">QUICK ACTIONS</p>
        <div className="quick-grid">
          <NavLink to="/services" className="quick-card"><ShoppingBag size={18} aria-hidden /><b>Pesan layanan</b><small>Instagram, TikTok, YouTube dan lainnya</small></NavLink>
          <NavLink to="/wallet" className="quick-card"><WalletIcon size={18} aria-hidden /><b>Top-up saldo</b><small>Gateway pembayaran aktif</small></NavLink>
          <NavLink to="/orders" className="quick-card"><ReceiptText size={18} aria-hidden /><b>Lihat pesanan</b><small>Riwayat dan status real-time</small></NavLink>
          <NavLink to="/refills" className="quick-card"><RefreshCw size={18} aria-hidden /><b>Refill</b><small>Ajukan refill layanan bergaransi</small></NavLink>
        </div>
      </section>
      <div className="grid-two mt-lg">
        <section className="panel activity-panel">
          <div className="section-head">
            <div><p className="eyebrow">LIVE QUEUE</p><h2>Aktivitas terakhir</h2></div>
            <NavLink data-testid="dashboard-orders-link" to="/orders" className="quiet-link">Lihat semua <ArrowUpRight size={14} aria-hidden /></NavLink>
          </div>
          {data.orders.length ? data.orders.map((o) => (
            <Link to={`/orders/${o.id}`} className="order-row" data-testid={`recent-order-${o.id}`} key={o.id}>
              <div className="service-icon"><Activity size={17} aria-hidden /></div>
              <div className="order-row-body"><strong>{o.service_name}</strong><small>{o.target} · {nfmt(o.quantity)} unit</small></div>
              <div className="row-end"><b>{money(o.price)}</b><StatusBadge status={o.status} /></div>
            </Link>
          )) : (
            <div className="empty">Belum ada pesanan. <Link to="/services" className="link-red">Pilih layanan</Link> untuk memulai.</div>
          )}
        </section>
        <section className="panel balance-panel">
          <p className="eyebrow">WALLET SNAPSHOT</p>
          <h2>{money(data.wallet.balance)}</h2>
          <div className="balance-line"><span>Saldo tertahan</span><b>{money(data.wallet.held)}</b></div>
          <div className="balance-line"><span>Notifikasi belum dibaca</span><b>{nfmt(data.metrics.unread)}</b></div>
          <div className="balance-line"><span>Provider</span><b>Demo</b></div>
          <div className="demo-label">● PROVIDER: AKTIF · SYNC 30s</div>
        </section>
      </div>
    </>
  );
}

// -------------------- SERVICES CATALOG --------------------
const SKELETON_ROWS = 6;
function Services() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Semua");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(50);
  useEffect(() => {
    let live = true;
    api.get("/services").then((r) => { if (live) setItems(r.data.items || []); }).catch(() => { if (live) setItems([]); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  useEffect(() => setLimit(50), [filter, q]);
  const cats = useMemo(() => {
    const counts = {};
    items.forEach((x) => { counts[x.category] = (counts[x.category] || 0) + 1; });
    return ["Semua", ...Object.keys(counts)];
  }, [items]);
  const filtered = items
    .filter((x) => (filter === "Semua" || x.category === filter) && x.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.price || 0) - (b.price || 0));
  const visible = filtered.slice(0, limit);
  const more = filtered.length - visible.length;
  return (
    <>
      <header className="page-head">
        <div>
          <p className="eyebrow">CATALOG</p>
          <h1>Pilih layanan.</h1>
          <p className="subtle">Pilih platform, lalu pilih layanan — diurutkan dari harga termurah.</p>
        </div>
      </header>
      <div className="filter-bar">
        <div className="search-wrap">
          <Search size={15} aria-hidden />
          <input data-testid="services-search-input" placeholder="Cari layanan..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className="cat-chips" role="tablist" aria-label="Kategori layanan">
        {cats.map((c) => {
          const count = c === "Semua" ? items.length : items.filter((x) => x.category === c).length;
          const label = c.replaceAll(" ", "-").replaceAll("/", "").toLowerCase();
          return (
            <button data-testid={`category-${label}`} key={c} className={filter === c ? "chip active" : "chip"} onClick={() => setFilter(c)} role="tab" aria-selected={filter === c}>
              <span>{c}</span><small>{count}</small>
            </button>
          );
        })}
      </div>
      {loading ? (
        <div className="service-list" aria-busy="true">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <div className="service-row skeleton-row" key={i}>
              <div className="skeleton line w40" />
              <div className="skeleton line w20" />
              <div className="skeleton pill-s" />
            </div>
          ))}
        </div>
      ) : (
        <div className="service-list">
          {visible.map((s) => (
            <Link data-testid={`service-card-${s.id}`} to={`/order/${s.id}`} className="service-row" key={s.id}>
              <div className="sr-main">
                <h3>{s.name}</h3>
                <p>{s.description || s.type}</p>
                <div className="service-meta">
                  <span>Min {nfmt(s.min)}</span>
                  <span>Max {nfmt(s.max)}</span>
                  {s.refill ? <span className="tag">Refill</span> : null}
                </div>
              </div>
              <div className="sr-price">
                <small>Mulai dari</small>
                {s.discount_percentage > 0 && <del>{money(s.base_price)}</del>}
                <strong>{money(s.price)}<em>/1K</em></strong>
                {s.discount_percentage > 0 && <span className="discount-badge">-{s.discount_percentage}% {s.tier_name}</span>}
              </div>
              <span data-testid={`service-order-${s.id}`} className="icon-button" aria-label={`Pesan ${s.name}`}><ArrowUpRight size={18} aria-hidden /></span>
            </Link>
          ))}
          {!filtered.length && <div className="empty full">Tidak ada layanan yang cocok. Coba ubah filter atau kata kunci.</div>}
          {more > 0 && (
            <button className="button outline load-more" onClick={() => setLimit((l) => l + 50)} data-testid="load-more-services">
              Muat lainnya — {nfmt(more)} layanan lagi
            </button>
          )}
        </div>
      )}
    </>
  );
}

// -------------------- ORDERING GUIDE --------------------
function OrderingGuide() {
  const steps = [
    ["Isi saldo", "Buka menu Wallet, pilih nominal top-up, lalu selesaikan pembayaran. Saldo akan masuk setelah pembayaran dikonfirmasi."],
    ["Pilih layanan", "Buka menu Layanan, gunakan pencarian atau kategori, kemudian pilih layanan yang sesuai kebutuhan Anda."],
    ["Periksa ketentuan", "Pastikan target, minimum, maksimum, jenis layanan, serta ketersediaan refill sudah sesuai sebelum lanjut."],
    ["Isi data pesanan", "Masukkan tautan atau username target dan jumlah pesanan. Untuk layanan khusus, isi data tambahan yang diminta."],
    ["Konfirmasi pesanan", "Periksa total harga dan saldo, lalu tekan Konfirmasi order. Saldo dipotong setelah pesanan berhasil dibuat."],
    ["Pantau status", "Lihat perkembangan di menu Pesanan. Jika layanan mendukung refill, ajukan dari detail pesanan setelah status selesai."],
  ];
  return (
    <>
      <header className="page-head"><div><p className="eyebrow">PANDUAN CLIENT</p><h1>Tatacara pemesanan.</h1><p className="subtle">Ikuti langkah berikut agar pesanan dapat diproses dengan benar.</p></div></header>
      <section className="ordering-guide">
        {steps.map(([title, description], index) => (
          <article className="panel ordering-step" key={title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><h2>{title}</h2><p>{description}</p></div>
          </article>
        ))}
      </section>
      <section className="panel guide-note"><LifeBuoy size={18} aria-hidden /><div><b>Butuh bantuan?</b><p>Jika pesanan bermasalah, kirim aduan melalui menu Aduan dengan menyertakan ID pesanan.</p></div><Link to="/tickets" className="button outline">Buat aduan</Link></section>
    </>
  );
}

// -------------------- ORDER PAGE (dynamic form) --------------------
function OrderPage({ id }) {
  const [service, setService] = useState(null);
  const [form, setForm] = useState({ target: "", quantity: 1000, comments: "", hashtag: "", answer_number: 1 });
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const [idem] = useState(() => uid());
  const [wallet, setWallet] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get(`/services/${id}`).then((r) => setService(r.data)).catch(() => setError("Layanan tidak ditemukan.")); api.get("/wallet").then((r) => setWallet(r.data)); }, [id]);
  if (!service && !error) return (
    <>
      <header className="page-head compact">
        <div><p className="eyebrow">NEW ORDER</p><h1 className="skeleton line w40" style={{ minHeight: 34 }} /></div>
      </header>
      <div className="order-layout" aria-busy="true">
        <div className="panel form-panel">
          {Array.from({ length: 4 }).map((_, i) => <div className="skeleton line" style={{ height: 64, marginBottom: 16 }} key={i} />)}
          <div className="skeleton pill-s" style={{ width: "100%", height: 46 }} />
        </div>
        <aside className="panel summary-panel">
          <div className="skeleton line w60" />
          <div className="skeleton line w80" style={{ height: 44, marginTop: 18 }} />
        </aside>
      </div>
    </>
  );
  if (error) return (
    <>
      <header className="page-head compact"><div><p className="eyebrow">NEW ORDER</p><h1>Layanan tidak ditemukan.</h1></div></header>
      <Link to="/services" className="button outline"><ArrowLeft size={15} aria-hidden /> Kembali ke katalog</Link>
    </>
  );
  const qty = Number(form.quantity) || 0;
  const total = Math.round((service.price * qty) / 1000);
  const insufficient = wallet && wallet.balance < total;
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const body = { service_id: service.id, target: form.target, quantity: qty };
      if (service.type === "Custom Comments") body.comments = form.comments;
      if (service.type === "Mentions Hashtag") body.hashtag = form.hashtag;
      if (service.type === "Poll") body.answer_number = Number(form.answer_number);
      const r = await api.post("/orders", body, { headers: { "Idempotency-Key": idem } });
      toast.success("Pesanan berhasil dibuat", { description: `${r.data.id} — sedang diproses.` });
      navigate(`/orders/${r.data.id}`);
    } catch (x) { setError(errorText(x)); } finally { setBusy(false); }
  };
  return (
    <>
      <header className="page-head compact">
        <div>
          <Link to="/services" className="back-link"><ArrowLeft size={15} aria-hidden /> Kembali ke katalog</Link>
          <p className="eyebrow">NEW ORDER / {service.category}</p>
          <h1>{service.name}</h1>
          <p className="subtle">{service.description}</p>
        </div>
        <span className="demo-label">● GUNTAGO</span>
      </header>
      <div className="order-layout">
        <form className="panel form-panel" onSubmit={submit}>
          <div className="form-section">
            <label>Target URL / username</label>
            <input data-testid="order-target-input" required placeholder="instagram.com/username" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} />
            <small className="hint">Isikan tautan post atau username sesuai layanan.</small>
          </div>
          <div className="form-section">
            <label>Jumlah unit</label>
            <input data-testid="order-quantity-input" type="number" min={service.min} max={service.max} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            <small className="hint">Minimum {nfmt(service.min)} · maksimum {nfmt(service.max)}</small>
          </div>
          {service.type === "Custom Comments" && (
            <div className="form-section">
              <label>Daftar komentar (satu per baris, total {qty} baris)</label>
              <textarea data-testid="order-comments-input" rows={6} placeholder={"Keren banget!\nMantap kak\nBagus konsepnya"} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} />
            </div>
          )}
          {service.type === "Mentions Hashtag" && (
            <div className="form-section">
              <label>Hashtag sumber (tanpa #)</label>
              <input data-testid="order-hashtag-input" placeholder="foryou" value={form.hashtag} onChange={(e) => setForm({ ...form, hashtag: e.target.value })} />
            </div>
          )}
          {service.type === "Poll" && (
            <div className="form-section">
              <label>Nomor jawaban poll</label>
              <input data-testid="order-answer-input" type="number" min={1} max={10} value={form.answer_number} onChange={(e) => setForm({ ...form, answer_number: e.target.value })} />
            </div>
          )}
          {error && <div data-testid="order-error-message" className="error">{error}</div>}
          {insufficient && <div className="warn">Saldo kurang. <Link to="/wallet" className="link-red">Top-up dulu</Link>.</div>}
          <button data-testid="order-submit-button" disabled={busy || !form.target || qty <= 0} className="button primary wide" type="submit">
            {busy ? "Memproses..." : "Konfirmasi order"} <ArrowUpRight size={17} aria-hidden />
          </button>
        </form>
        <aside className="panel summary-panel">
          <p className="eyebrow">ORDER SUMMARY</p>
          <div className="summary-service">
            <span>{service.name}</span>
            <span>{service.refill ? "Refill tersedia" : "Standard delivery"}</span>
            <span className="mono-tiny">TYPE · {service.type}</span>
          </div>
          <div className="total">
            <small>Total estimasi</small>
            <strong data-testid="order-total-estimate">{money(total)}</strong>
            {service.discount_percentage > 0 && <span className="discount-badge">Diskon {service.tier_name}: {service.discount_percentage}%</span>}
          </div>
          <div className="summary-line"><span>Saldo saat ini</span><b>{money(wallet?.balance || 0)}</b></div>
          <div className="summary-line"><span>Sisa setelah order</span><b>{money((wallet?.balance || 0) - total)}</b></div>
          <div className="summary-note"><ShieldCheck size={16} aria-hidden /> Saldo akan dipotong setelah order dikonfirmasi. Idempotency-key mencegah order ganda.</div>
        </aside>
      </div>
    </>
  );
}

// -------------------- ORDERS LIST --------------------
function Orders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(""); const [statusFilter, setStatusFilter] = useState("Semua");
  const load = useCallback(() => api.get("/orders").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false)), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const filtered = items.filter((o) => (statusFilter === "Semua" || o.status === statusFilter) && (q ? (o.service_name + o.target + o.id).toLowerCase().includes(q.toLowerCase()) : true));
  const statuses = ["Semua", "Pending", "Processing", "Success", "Partial", "Error"];
  return (
    <>
      <header className="page-head">
        <div>
          <p className="eyebrow">ORDERS / HISTORY</p>
          <h1>Pesananmu.</h1>
          <p className="subtle">Auto-refresh setiap 15 detik · Provider mensinkron status setiap 30 detik.</p>
        </div>
        <NavLink data-testid="orders-new-order-button" to="/services" className="button primary"><Plus size={18} aria-hidden /> Order baru</NavLink>
      </header>
      <div className="filter-bar">
        <div className="search-wrap"><Search size={15} aria-hidden /><input placeholder="Cari ID / layanan / target..." value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="pills">
          {statuses.map((s) => (
            <button key={s} className={statusFilter === s ? "pill active" : "pill"} onClick={() => setStatusFilter(s)}>{STATUS_META[s]?.label || s}</button>
          ))}
        </div>
      </div>
      <section className="panel table-panel">
        <div className="table-head"><span>LAYANAN</span><span>TARGET</span><span className="tr">NILAI</span><span>STATUS</span></div>
        {loading ? Array.from({ length: 4 }).map((_, i) => (
          <div className="table-row" key={i} aria-hidden="true">
            <strong><span className="skeleton line w60" /><span className="skeleton line w30" style={{ marginTop: 6 }} /></strong>
            <span className="skeleton line w40" />
            <span className="skeleton line w20" />
            <span className="skeleton pill-s" />
          </div>
        )) : filtered.map((o) => (
          <Link data-testid={`order-row-${o.id}`} className="table-row" to={`/orders/${o.id}`} key={o.id}>
            <strong>{o.service_name}<small>{o.id} · {fmtDate(o.created_at)}</small></strong>
            <span className="truncate">{o.target}</span>
            <b className="tr">{money(o.price)}</b>
            <span><StatusBadge status={o.status} /></span>
          </Link>
        ))}
        {!loading && !filtered.length && <div className="empty">Riwayat pesanan akan muncul di sini.</div>}
      </section>
    </>
  );
}

// -------------------- ORDER DETAIL --------------------
function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null); const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((x) => {
    const msg = x?.response?.status === 404 ? "Pesanan tidak ditemukan. Mungkin bukan milikmu, atau sudah dihapus." : errorText(x);
    setError(msg);
  }), [id]);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);
  if (error) return (
    <>
      <header className="page-head compact"><div><p className="eyebrow">ORDER DETAIL</p><h1>Pesanan tidak ditemukan.</h1></div></header>
      <section className="panel">
        <p className="subtle">{error}</p>
        <Link to="/orders" className="button outline mt"><ArrowLeft size={15} aria-hidden /> Kembali ke daftar pesanan</Link>
      </section>
    </>
  );
  if (!order) return <div className="loading">Memuat detail pesanan...</div>;
  const askRefill = async () => {
    setBusy(true);
    try { await api.post(`/orders/${id}/refill`); toast.success("Refill dibuat", { description: "Permintaan Anda sedang diproses." }); load(); }
    catch (x) { toast.error("Layanan refill sedang mengalami gangguan. Silakan coba lagi beberapa saat lagi."); } finally { setBusy(false); }
  };
  return (
    <>
      <header className="page-head compact">
        <div>
          <p className="eyebrow">ORDER DETAIL / {order.category}</p>
          <h1>{order.service_name}</h1>
          <p className="subtle">{order.id} · dibuat {fmtDate(order.created_at)}</p>
        </div>
        <StatusBadge status={order.status} />
      </header>
      <div className="detail-grid">
        <section className="panel">
          <p className="eyebrow">DETAIL</p>
          <div className="kv"><span>Target</span><b>{order.target}</b></div>
          <div className="kv"><span>Jumlah</span><b>{nfmt(order.quantity)} unit</b></div>
          <div className="kv"><span>Start count</span><b>{nfmt(order.start_count)}</b></div>
          <div className="kv"><span>Sisa</span><b>{nfmt(order.remains)}</b></div>
          <div className="kv"><span>Tipe layanan</span><b>{order.type}</b></div>
          {order.extra?.comments && (
            <div className="kv col">
              <span>Komentar</span>
              <pre className="mono-block">{order.extra.comments}</pre>
            </div>
          )}
          {order.extra?.hashtag && <div className="kv"><span>Hashtag</span><b>#{order.extra.hashtag}</b></div>}
          {order.extra?.answer_number && <div className="kv"><span>Jawaban Poll</span><b>#{order.extra.answer_number}</b></div>}
          {order.refill && (
            <button data-testid="order-refill-button" className="button outline mt" onClick={askRefill} disabled={busy || !["Success", "Partial"].includes(order.status)}>
              <RefreshCw size={15} aria-hidden /> {busy ? "Memproses..." : "Request Refill"}
            </button>
          )}
        </section>
        <section className="panel">
          <p className="eyebrow">TIMELINE</p>
          {(order.history || []).map((h, i) => (
            <div className="timeline-row" key={i}>
              <span className="dot" />
              <div><b>{STATUS_META[h.status]?.label || h.status}</b><small>{fmtDate(h.at)}</small></div>
            </div>
          ))}
          {!order.history?.length && <div className="empty">Belum ada perubahan status.</div>}
          {order.refills?.length > 0 && (
            <>
              <p className="eyebrow mt-lg">REFILL</p>
              {order.refills.map((r) => (
                <div className="kv" key={r.id}><span className="mono">{r.id}</span><StatusBadge status={r.status} /></div>
              ))}
            </>
          )}
        </section>
        <section className="panel summary-panel">
          <p className="eyebrow">CHARGES</p>
          <div className="summary-line"><span>Nilai order</span><b>{money(order.price)}</b></div>
          <div className="summary-note"><ShieldCheck size={16} aria-hidden /> Saldo telah dipotong saat order dibuat.</div>
        </section>
      </div>
    </>
  );
}

// -------------------- REFILLS --------------------
function Refills() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/refills").then((r) => setItems(r.data.items || [])); }, []);
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">REFILLS</p><h1>Permintaan refill.</h1><p className="subtle">Status refill diperbarui otomatis setiap 45 detik.</p></div>
      </header>
      <section className="panel table-panel">
        <div className="table-head"><span>REFILL ID</span><span>ORDER</span><span>DIBUAT</span><span>STATUS</span></div>
        {items.map((r) => (
          <Link className="table-row" to={`/orders/${r.order_id}`} key={r.id} data-testid={`refill-row-${r.id}`}>
            <strong className="mono">{r.id}</strong>
            <span className="mono">{r.order_id}</span>
            <span>{fmtDate(r.created_at)}</span>
            <span><StatusBadge status={r.status} /></span>
          </Link>
        ))}
        {!items.length && <div className="empty">Belum ada permintaan refill. Ajukan refill dari halaman detail pesanan.</div>}
      </section>
    </>
  );
}

// -------------------- TICKETS (ADUAN) --------------------
const TicketBadge = ({ status }) => {
  const m = TICKET_META[status] || { label: status, cls: "processing" };
  return <span className={`status ${m.cls}`}>{m.label}</span>;
};

function Tickets() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ subject: "", category: "Pesanan", message: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tickets").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!nf.subject || nf.message.length < 10) { toast.error("Judul dan isi aduan wajib diisi (min. 10 karakter)"); return; }
    setBusy(true);
    try { const r = await api.post("/tickets", nf); toast.success("Aduan terkirim", { description: `${r.data.id} — admin akan merespons.` }); setShowNew(false); setNf({ subject: "", category: "Pesanan", message: "" }); load(); }
    catch (x) { toast.error(errorText(x)); } finally { setBusy(false); }
  };
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">TICKETS / ADUAN</p><h1>Aduan & bantuan.</h1><p className="subtle">Kendala pesanan, refill, atau deposit? Kirim aduan dan pantau respons admin di sini.</p></div>
        <button className="button primary" onClick={() => setShowNew(!showNew)} data-testid="new-ticket-button"><Plus size={15} aria-hidden /> {showNew ? "Tutup" : "Buat aduan"}</button>
      </header>
      {showNew && (
        <section className="panel form-panel mt-lg">
          <p className="eyebrow">ADUAN BARU</p>
          <div className="two-col">
            <div className="form-section"><label>Kategori</label>
              <select value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} data-testid="ticket-category-select">
                {TICKET_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-section"><label>Judul aduan</label><input placeholder="Mis. Order tidak masuk" value={nf.subject} onChange={(e) => setNf({ ...nf, subject: e.target.value })} data-testid="ticket-subject-input" /></div>
          </div>
          <div className="form-section"><label>Detail kendala</label><textarea rows={5} placeholder="Jelaskan kendala beserta ID pesanan/deposit bila ada..." value={nf.message} onChange={(e) => setNf({ ...nf, message: e.target.value })} data-testid="ticket-message-input" /></div>
          <button className="button primary" onClick={create} disabled={busy} data-testid="ticket-submit-button">{busy ? "Mengirim..." : "Kirim aduan"} <ArrowUpRight size={16} aria-hidden /></button>
        </section>
      )}
      <section className="panel table-panel mt-lg">
        <div className="table-head simple"><span>ADUAN</span><span>KATEGORI</span><span>STATUS</span></div>
        {items.map((t) => (
          <Link className="table-row simple" to={`/tickets/${t.id}`} key={t.id} data-testid={`ticket-row-${t.id}`}>
            <strong>{t.subject}<small>{t.id} · {fmtDate(t.created_at)} · {t.replies.length} balasan</small></strong>
            <span>{t.category}</span>
            <span><TicketBadge status={t.status} /></span>
          </Link>
        ))}
        {!items.length && <div className="empty">Belum ada aduan. Jika ada kendala, buat aduan baru.</div>}
      </section>
    </>
  );
}

function TicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/tickets/${id}`).then((r) => setTicket(r.data)).catch((x) => setErr(errorText(x))), [id]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (err) return (
    <>
      <header className="page-head compact"><div><p className="eyebrow">TICKET DETAIL</p><h1>Aduan tidak ditemukan.</h1></div></header>
      <section className="panel"><p className="subtle">{err}</p><Link to="/tickets" className="button outline mt"><ArrowLeft size={15} aria-hidden /> Kembali</Link></section>
    </>
  );
  if (!ticket) return <div className="loading">Memuat aduan...</div>;
  const closed = ticket.status === "closed" || ticket.status === "resolved";
  const sendReply = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/tickets/${id}/reply`, { message: msg }); setMsg(""); toast.success("Balasan terkirim"); load(); }
    catch (x) { toast.error(errorText(x)); } finally { setBusy(false); }
  };
  const closeTicket = async () => {
    if (!window.confirm("Tutup aduan ini? Aduan tertutup tidak bisa dibalas lagi.")) return;
    try { await api.post(`/tickets/${id}/close`); toast.success("Aduan ditutup"); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const Thread = ({ sender, name, message, at }) => (
    <div className={`ticket-msg ${sender === "admin" ? "admin" : ""}`}>
      <b>{name} <small className="mono">{sender === "admin" ? "ADMIN" : "ANDA"} · {fmtDate(at)}</small></b>
      <p>{message}</p>
    </div>
  );
  return (
    <>
      <header className="page-head compact">
        <div>
          <Link to="/tickets" className="back-link"><ArrowLeft size={15} aria-hidden /> Kembali ke daftar aduan</Link>
          <p className="eyebrow">TICKET / {ticket.id} · {ticket.category}</p>
          <h1>{ticket.subject}</h1>
        </div>
        <TicketBadge status={ticket.status} />
      </header>
      <div className="ticket-layout">
        <section className="panel ticket-thread">
          <p className="eyebrow">PERCAKAPAN</p>
          <Thread sender="user" name={ticket.user_email} message={ticket.message} at={ticket.created_at} />
          {(ticket.replies || []).map((r) => <Thread key={r.id} sender={r.sender} name={r.name} message={r.message} at={r.created_at} />)}
          {!closed && (
            <div className="form-section mt-lg">
              <label>Balas aduan</label>
              <textarea rows={3} placeholder="Tulis balasan..." value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="ticket-reply-input" />
              <div className="row-actions mt">
                <button className="button primary" onClick={sendReply} disabled={busy || !msg.trim()} data-testid="ticket-reply-button">{busy ? "Mengirim..." : "Kirim balasan"}</button>
              </div>
            </div>
          )}
        </section>
        <aside className="panel summary-panel">
          <p className="eyebrow">INFORMASI</p>
          <div className="kv"><span>Status</span><TicketBadge status={ticket.status} /></div>
          <div className="kv"><span>Kategori</span><b>{ticket.category}</b></div>
          <div className="kv"><span>Dibuat</span><b>{fmtDate(ticket.created_at)}</b></div>
          <div className="kv"><span>Balasan</span><b>{ticket.replies.length}</b></div>
          {ticket.status === "closed" && <div className="summary-note"><ShieldCheck size={16} aria-hidden /> Aduan ditutup. Buat aduan baru bila kendala berlanjut.</div>}
          {ticket.status !== "closed" && (
            <button className="button outline wide" onClick={closeTicket} data-testid="ticket-close-button"><Trash2 size={15} aria-hidden /> Tutup aduan</button>
          )}
        </aside>
      </div>
    </>
  );
}

// -------------------- WALLET --------------------
function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [pm, setPm] = useState(null);
  const [amount, setAmount] = useState(100000);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([
    api.get("/wallet").then((r) => setWallet(r.data)),
    api.get("/transactions").then((r) => setTransactions(r.data.items || [])),
    api.get("/deposits").then((r) => setDeposits(r.data.items || [])),
    api.get("/payment/methods").then((r) => setPm(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const gatewayLabel = pm ? (pm.provider === "tripay" ? "Tripay" : pm.provider === "midtrans" ? "Midtrans" : "Demo") : "Demo";
  const gatewayMode = pm ? (pm.provider === "demo" ? "" : ` ${String(pm.mode || "sandbox").toUpperCase()}`) : "";
  const deposit = async () => {
    setBusy(true);
    const win = window.open("", "_blank"); // stub tab dulu agar tidak kena popup blocker
    try {
      const r = await api.post("/deposits", { amount: Number(amount) });
      if (r.data.redirect_url) {
        toast.success(`Checkout ${gatewayLabel} dibuka di tab baru`);
        if (win) win.location.href = r.data.redirect_url;
        else window.open(r.data.redirect_url, "_blank");
        load();
      } else {
        if (win) win.close();
        toast.info("Mode demo aktif — konfirmasi manual", { description: `Deposit ${r.data.id} dibuat.` });
        load();
      }
    } catch (x) { if (win) win.close(); toast.error(errorText(x)); } finally { setBusy(false); }
  };
  const confirmDemo = async (did) => {
    try { await api.post(`/deposits/${did}/confirm-demo`); toast.success("Saldo telah dikreditkan"); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">WALLET / DEPOSIT</p><h1>Isi saldo.</h1><p className="subtle">Deposit diproses via {gatewayLabel}{gatewayMode || " (demo)"} — saldo masuk otomatis setelah pembayaran.</p></div>
      </header>
      <div className="wallet-layout">
        <section className="wallet-hero">
          <p className="eyebrow">SALDO TERSEDIA</p>
          <strong data-testid="wallet-balance">{money(wallet?.balance || 0)}</strong>
          <span>Saldo terakhir diperbarui {fmtDate(wallet?.updated_at)}</span>
          <div className="wallet-hero-foot"><WalletIcon size={14} aria-hidden /> {transactions.length} transaksi tercatat</div>
        </section>
        <section className="panel deposit-panel">
          <p className="eyebrow">CHECKOUT · {gatewayLabel.toUpperCase()}</p>
          <h2>Pilih nominal deposit</h2>
          <div className="amounts">
            {[50000, 100000, 250000, 500000, 1000000, 2500000].map((x) => (
              <button data-testid={`deposit-amount-${x}`} className={amount === x ? "amount active" : "amount"} key={x} onClick={() => setAmount(x)}>{money(x)}</button>
            ))}
          </div>
          <div className="form-section">
            <label>Atau masukkan nominal custom (min Rp 10.000)</label>
            <input data-testid="deposit-custom-input" type="number" min={10000} value={amount} onChange={(e) => setAmount(Number(e.target.value) || 0)} />
          </div>
          <button data-testid="deposit-submit-button" className="button primary wide" disabled={busy || amount < 10000} onClick={deposit}>
            <WalletCards size={17} aria-hidden /> {busy ? "Memproses..." : `Buat checkout ${money(amount)}`}
          </button>
          <div className="demo-label">● {gatewayLabel.toUpperCase()}{gatewayMode} — pembayaran diverifikasi otomatis</div>
        </section>
      </div>
      <div className="grid-two mt-lg">
        <section className="panel table-panel">
          <div className="table-head simple"><span>DEPOSIT</span><span>NOMINAL</span><span>STATUS</span></div>
          {deposits.slice(0, 10).map((d) => (
            <div className="table-row" key={d.id} data-testid={`deposit-row-${d.id}`}>
              <strong className="mono">{d.id}<small>{fmtDate(d.created_at)} · {d.method}</small></strong>
              <b className="mono">{money(d.amount)}</b>
              <span className="row-actions">
                <span className={`status ${d.status === "PAID" ? "available" : d.status === "PENDING" ? "processing" : "danger"}`}>{d.status}</span>
                {d.status === "PENDING" && d.mode === "DEMO" && (
                  <button className="mini-button" data-testid={`deposit-confirm-${d.id}`} onClick={() => confirmDemo(d.id)}>Konfirmasi demo</button>
                )}
                {d.status === "PENDING" && d.redirect_url && (
                  <a className="mini-button" href={d.redirect_url} target="_blank" rel="noreferrer">Buka pembayaran</a>
                )}
              </span>
            </div>
          ))}
          {!deposits.length && <div className="empty">Belum ada deposit.</div>}
        </section>
        <section className="panel table-panel">
          <div className="table-head simple"><span>WAKTU</span><span>KETERANGAN</span><span>NILAI</span></div>
          {transactions.slice(0, 10).map((t) => (
            <div className="table-row" key={t.id} data-testid={`transaction-row-${t.id}`}>
              <span className="mono">{fmtDate(t.created_at)}</span>
              <span>{t.description}<small className="mono">{t.id}</small></span>
              <b className={`tr ${t.type === "CREDIT" ? "text-green" : "text-red"}`}>{t.type === "CREDIT" ? "+" : "-"} {money(t.amount)}</b>
            </div>
          ))}
          {!transactions.length && <div className="empty">Belum ada transaksi tercatat.</div>}
        </section>
      </div>
    </>
  );
}

// -------------------- NOTIFICATIONS --------------------
function Notifications({ refresh }) {
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(5);
  const load = () => api.get("/notifications").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); refresh(); };
  const markAll = async () => {
    try { const r = await api.post("/notifications/read-all"); toast.success(r.data.affected ? `${r.data.affected} notifikasi ditandai dibaca` : "Semua notifikasi sudah dibaca"); load(); refresh(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const unread = items.filter((n) => !n.read).length;
  const visible = items.slice(0, limit);
  const more = items.length - visible.length;
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">NOTIFICATIONS</p><h1>Notifikasi.</h1><p className="subtle">Pembaruan status pesanan, refill, dan deposit tercatat di sini.</p></div>
        {unread > 0 && (
          <button className="button outline" onClick={markAll} data-testid="notifications-mark-all-button"><CheckCheck size={15} aria-hidden /> Tandai semua dibaca ({nfmt(unread)})</button>
        )}
      </header>
      <section className="panel table-panel">
        {visible.map((n) => (
          <div className={`notification-row ${n.read ? "" : "unread"}`} key={n.id} data-testid={`notification-${n.id}`}>
            <span className={`nd ${n.kind}`} />
            <div>
              <strong>{n.title}</strong>
              <p>{n.body}</p>
              <small>{fmtDate(n.created_at)}</small>
            </div>
            {!n.read && <button className="mini-button" onClick={() => markRead(n.id)}>Tandai dibaca</button>}
          </div>
        ))}
        {!items.length && <div className="empty">Belum ada notifikasi.</div>}
      </section>
      {more > 0 && (
        <button className="button outline load-more" onClick={() => setLimit((l) => l + 10)} data-testid="load-more-notifications">
          Muat lainnya — {nfmt(more)} notifikasi lagi
        </button>
      )}
    </>
  );
}

// -------------------- ADMIN --------------------
function AdminOverview() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="loading">Memuat admin...</div>;
  const syncNow = async () => { setSyncing(true); try { await api.post("/admin/sync-now"); toast.success("Sinkronisasi selesai"); load(); } catch (x) { toast.error(errorText(x)); } finally { setSyncing(false); } };
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">ADMIN / CONTROL ROOM</p><h1>Operasional.</h1><p className="subtle">Pantau antrean dan kesehatan provider dari satu ruang.</p></div>
        <span className="demo-label">● PROVIDER: {data.provider}</span>
      </header>
      <section className="metrics">
        <Metric label="Total user" value={nfmt(data.users)} detail="Akun terdaftar" />
        <Metric label="Total order" value={nfmt(data.orders)} detail="Semua status" accent="metric-primary" />
        <Metric label="Aktif" value={nfmt(data.active)} detail="Pending + Processing" />
        <Metric label="Deposit pending" value={nfmt(data.deposits)} detail="Perlu rekonsiliasi" />
      </section>
      <section className="metrics mt-lg">
        <Metric label="Revenue" value={money(data.revenue)} detail="Total order tersimpan" accent="metric-primary" />
        <Metric label="Profit" value={money(data.profit)} detail="Selisih harga vs modal" />
        <Metric label="Margin %" value={data.revenue ? `${Math.round((data.profit / data.revenue) * 100)}%` : "0%"} detail="Rata-rata semua order" />
        <Metric label="Provider" value="Demo" detail="Adapter aktif" />
      </section>
      <section className="admin-grid mt-lg">
        <div className="panel health">
          <p className="eyebrow">PROVIDER HEALTH</p>
          <h2>Adapter siap digunakan</h2>
          <div className="health-row"><span className="pulse" /><b>{data.provider}</b><StatusBadge status="Success" /></div>
          <p>{data.last_sync}</p>
          <button data-testid="admin-sync-button" className="button outline" onClick={syncNow} disabled={syncing}><RefreshCw size={15} aria-hidden /> {syncing ? "Menyinkronkan..." : "Sync sekarang"}</button>
        </div>
        <div className="panel admin-actions">
          <p className="eyebrow">QUICK ACCESS</p>
          <NavLink data-testid="admin-users-link" to="/admin/users"><Users size={18} aria-hidden /> Kelola pengguna <ArrowUpRight size={15} aria-hidden /></NavLink>
          <NavLink data-testid="admin-services-link" to="/admin/services"><ShoppingBag size={18} aria-hidden /> Kelola layanan <ArrowUpRight size={15} aria-hidden /></NavLink>
          <NavLink data-testid="admin-orders-link" to="/admin/orders"><ReceiptText size={18} aria-hidden /> Lihat semua order <ArrowUpRight size={15} aria-hidden /></NavLink>
          <NavLink data-testid="admin-deposits-link" to="/admin/deposits"><WalletCards size={18} aria-hidden /> Deposit masuk <ArrowUpRight size={15} aria-hidden /></NavLink>
        </div>
      </section>
    </>
  );
}

function AdminUsers() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [adj, setAdj] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); api.get("/admin/settings/tiers").then((r) => setTiers(r.data.tiers || [])); }, []);
  const save = async () => {
    try { const r = await api.post(`/admin/users/${adj.id}/balance`, { amount: Number(adj.amount), reason: adj.reason || "Admin adjust" }); toast.success(`Saldo diperbarui: ${new Intl.NumberFormat("id-ID",{style:"currency",currency:"IDR",maximumFractionDigits:0}).format(r.data.balance)}`); setAdj(null); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const suspend = async (u) => {
    try { const r = await api.post(`/admin/users/${u.id}/suspend`); toast.success(r.data.suspended ? "Pengguna ditangguhkan" : "Pengguna diaktifkan"); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const setTier = async (u, tier) => {
    try { await api.patch(`/admin/users/${u.id}/tier`, { tier: tier || null }); toast.success("Tier pengguna diperbarui"); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Hapus akun ${u.email}? Aksi ini permanen.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  return (
    <>
      <header className="page-head"><div><p className="eyebrow">ADMIN / USERS</p><h1>Pengguna.</h1><p className="subtle">Atur saldo, tier diskon, tangguhkan, atau hapus akun pengguna.</p></div></header>
      <section className="panel table-panel">
        <div className="table-head user-table"><span>NAMA</span><span>EMAIL</span><span>TIER / DISKON</span><span className="tr">SALDO</span><span>AKSI</span></div>
        {items.map((u) => (
          <div className={`table-row user-table ${u.suspended ? "suspended" : ""}`} key={u.id} data-testid={`admin-user-${u.email}`}>
            <strong>{u.name}<small>Sejak {fmtDate(u.created_at)} · <span className={`status ${u.role === "admin" ? "danger" : u.suspended ? "warning" : "available"}`}>{u.suspended ? "Suspended" : u.role}</span></small></strong>
            <span>{u.email}</span>
            <span>
              {u.role === "admin" ? <small className="hint">Administrator</small> : (
                <select className="tier-select" value={u.tier || ""} onChange={(e) => setTier(u, e.target.value)} data-testid={`admin-user-tier-${u.email}`}>
                  <option value="">Tanpa tier (0%)</option>
                  {tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name} ({tier.discount}%)</option>)}
                </select>
              )}
            </span>
            <b className="tr mono">{money(u.balance)}</b>
            <span className="row-actions">
              {adj?.id === u.id ? (
                <>
                  <input className="inline-input" placeholder="+/- Rp" type="number" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} data-testid={`admin-user-adjust-${u.email}`} />
                  <button className="mini-button" onClick={save}>Simpan</button>
                  <button className="mini-button ghost" onClick={() => setAdj(null)}>Batal</button>
                </>
              ) : (
                <>
                  <button className="mini-button" onClick={() => setAdj({ id: u.id, amount: "", reason: "Top-up manual" })} data-testid={`admin-user-adjust-btn-${u.email}`}>Saldo</button>
                  {u.role !== "admin" && (
                    <>
                      <button className="mini-button ghost" onClick={() => suspend(u)} data-testid={`admin-user-suspend-${u.email}`}>{u.suspended ? "Aktifkan" : "Tangguhkan"}</button>
                      <button className="mini-button ghost text-red" onClick={() => remove(u)} data-testid={`admin-user-delete-${u.email}`}>Hapus</button>
                    </>
                  )}
                </>
              )}
            </span>
          </div>
        ))}
      </section>
    </>
  );
}

function AdminServices() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ name: "", category: "Instagram", description: "", type: "Default", price: 50000, provider_cost: 40000, min: 100, max: 10000, refill: false });
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [sel, setSel] = useState(new Set());
  const PAGE = 50;
  const fetchPage = useCallback(async (query, off, append) => {
    const r = await api.get("/admin/services", { params: { q: query, offset: off, limit: PAGE } });
    setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
    setTotal(r.data.total);
    setOffset(off + r.data.items.length);
  }, []);
  const load = () => fetchPage(q, 0, false);
  useEffect(() => {
    const t = setTimeout(() => fetchPage(q, 0, false), 250);
    return () => clearTimeout(t);
  }, [q, fetchPage]);
  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data.items || [])); }, []);
  const toggle = async (s) => { await api.patch(`/admin/services/${s.id}`, { active: !s.active }); toast.success(`Layanan ${!s.active ? "diaktifkan" : "dinonaktifkan"}`); load(); };
  const savePrice = async () => { if (!edit) return; try { await api.patch(`/admin/services/${edit.id}`, { price: Number(edit.price) }); toast.success("Harga diperbarui"); setEdit(null); load(); } catch (x) { toast.error(errorText(x)); } };
  const remove = async (s) => { if (!window.confirm(`Hapus layanan "${s.name}"?`)) return; await api.delete(`/admin/services/${s.id}`); toast.success("Layanan dihapus"); load(); };
  const create = async () => { try { await api.post("/admin/services", nf); toast.success("Layanan dibuat"); setShowNew(false); setNf({ ...nf, name: "" }); load(); } catch (x) { toast.error(errorText(x)); } };
  const syncFromProvider = async () => {
    setSyncing(true);
    try { const r = await api.post("/admin/provider/sync-services"); toast.success(`Import selesai: ${r.data.imported} baru, ${r.data.updated} diperbarui, ${r.data.categories} kategori`); load(); }
    catch (x) { toast.error(errorText(x)); } finally { setSyncing(false); }
  };
  const clearAll = async () => {
    if (!window.confirm(`Hapus SEMUA ${total} layanan? Aksi ini permanen dan tidak bisa dibatalkan.`)) return;
    try { const r = await api.post("/admin/services/clear"); toast.success(`${r.data.deleted} layanan dihapus`); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const bulk = async (action) => {
    if (!sel.size) { toast.error("Pilih layanan terlebih dahulu"); return; }
    if (action === "delete" && !window.confirm(`Hapus ${sel.size} layanan terpilih? Aksi ini permanen.`)) return;
    try { const r = await api.post("/admin/services/bulk", { ids: Array.from(sel), action }); toast.success(`${r.data.affected} layanan ${action === "delete" ? "dihapus" : action === "activate" ? "diaktifkan" : action === "deactivate" ? "dinonaktifkan" : "di-reprice"}`); setSel(new Set()); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const toggleSel = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  const selectAll = () => setSel(sel.size === items.length ? new Set() : new Set(items.map((s) => s.id)));
  const loadMore = () => fetchPage(q, offset, true);
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">ADMIN / SERVICES</p><h1>Kelola layanan.</h1><p className="subtle">Import dari provider, ubah harga & margin, aksi massal, atau tambah manual.</p></div>
        <div className="header-actions">
          <button className="button outline" onClick={syncFromProvider} disabled={syncing} data-testid="admin-sync-services-button"><RefreshCw size={15} aria-hidden /> {syncing ? "Import..." : "Import dari provider"}</button>
          <button className="button outline text-red" onClick={clearAll} disabled={!items.length} data-testid="admin-clear-services-button"><Trash2 size={15} aria-hidden /> Hapus semua</button>
          <button className="button primary" onClick={() => setShowNew(!showNew)} data-testid="admin-new-service-button"><Plus size={15} aria-hidden /> Tambah manual</button>
        </div>
      </header>
      {showNew && (
        <section className="panel form-panel mt-lg">
          <p className="eyebrow">LAYANAN BARU</p>
          <div className="two-col">
            <div className="form-section"><label>Nama</label><input value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} data-testid="admin-service-name-input" /></div>
            <div className="form-section"><label>Kategori</label><input list="cat-list" value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} /><datalist id="cat-list">{cats.map((c) => <option key={c.id} value={c.name} />)}</datalist></div>
            <div className="form-section"><label>Tipe</label><select value={nf.type} onChange={(e) => setNf({ ...nf, type: e.target.value })}><option>Default</option><option>Package</option><option>Custom Comments</option><option>Mentions Custom List</option><option>Mentions Hashtag</option><option>Mentions User Followers</option><option>Mentions Media Likers</option><option>Poll</option><option>Comment Replies</option></select></div>
            <div className="form-section"><label>Harga jual /1K</label><input type="number" value={nf.price} onChange={(e) => setNf({ ...nf, price: Number(e.target.value) })} /></div>
            <div className="form-section"><label>Modal /1K</label><input type="number" value={nf.provider_cost} onChange={(e) => setNf({ ...nf, provider_cost: Number(e.target.value) })} /></div>
            <div className="form-section"><label>Min</label><input type="number" value={nf.min} onChange={(e) => setNf({ ...nf, min: Number(e.target.value) })} /></div>
            <div className="form-section"><label>Max</label><input type="number" value={nf.max} onChange={(e) => setNf({ ...nf, max: Number(e.target.value) })} /></div>
            <div className="form-section"><label>Refill</label><select value={nf.refill ? "1" : "0"} onChange={(e) => setNf({ ...nf, refill: e.target.value === "1" })}><option value="0">Tidak</option><option value="1">Ya</option></select></div>
          </div>
          <div className="form-section"><label>Deskripsi</label><textarea rows={2} value={nf.description} onChange={(e) => setNf({ ...nf, description: e.target.value })} /></div>
          <button className="button primary" onClick={create} data-testid="admin-service-create-submit">Simpan layanan</button>
        </section>
      )}
      <div className="filter-bar mt-lg">
        <div className="search-wrap"><Search size={15} aria-hidden /><input placeholder="Cari nama/kategori/ID..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-services-search-input" /></div>
        <span className="mono-tiny">Menampilkan {items.length} / {nfmt(total)} layanan · {sel.size} dipilih</span>
      </div>
      {sel.size > 0 && (
        <div className="bulk-bar" data-testid="bulk-bar">
          <span><b>{sel.size}</b> dipilih</span>
          <div className="row-actions">
            <button className="mini-button" onClick={() => bulk("activate")} data-testid="bulk-activate">Aktifkan</button>
            <button className="mini-button" onClick={() => bulk("deactivate")} data-testid="bulk-deactivate">Nonaktifkan</button>
            <button className="mini-button" onClick={() => bulk("reprice")} data-testid="bulk-reprice">Reprice</button>
            <button className="mini-button text-red" onClick={() => bulk("delete")} data-testid="bulk-delete">Hapus</button>
            <button className="mini-button ghost" onClick={() => setSel(new Set())}>Batal</button>
          </div>
        </div>
      )}
      <section className="panel table-panel">
        <div className="table-head with-check"><span><input type="checkbox" checked={sel.size > 0 && sel.size === items.length} onChange={selectAll} data-testid="select-all-checkbox" /></span><span>LAYANAN</span><span>KATEGORI</span><span className="tr">HARGA / MODAL</span><span>AKSI</span></div>
        {items.map((s) => (
          <div className="table-row with-check" key={s.id} data-testid={`admin-service-${s.id}`}>
            <span><input type="checkbox" checked={sel.has(s.id)} onChange={() => toggleSel(s.id)} data-testid={`select-${s.id}`} /></span>
            <strong>{s.name}<small>{s.id} · {s.type}{s.provider_service_id ? ` · PROV#${s.provider_service_id}` : ""}</small></strong>
            <span>{s.category}</span>
            <div className="tr">
              {edit?.id === s.id ? (
                <input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} className="inline-input" />
              ) : (<b className="mono">{money(s.price)}</b>)}
              <small className="mono">Modal {money(s.provider_cost)} · margin {s.provider_cost ? Math.round((s.price - s.provider_cost) / s.provider_cost * 100) : 0}%</small>
            </div>
            <span className="row-actions">
              <span className={`status ${s.active ? "available" : "danger"}`}>{s.active ? "Aktif" : "Nonaktif"}</span>
              {edit?.id === s.id ? (
                <>
                  <button className="mini-button" onClick={savePrice}>Simpan</button>
                  <button className="mini-button ghost" onClick={() => setEdit(null)}>Batal</button>
                </>
              ) : (
                <>
                  <button className="mini-button" onClick={() => setEdit({ id: s.id, price: s.price })}>Harga</button>
                  <button className="mini-button ghost" onClick={() => toggle(s)}>{s.active ? "Off" : "On"}</button>
                  <button className="mini-button ghost text-red" onClick={() => remove(s)} data-testid={`admin-service-delete-${s.id}`}>Hapus</button>
                </>
              )}
            </span>
          </div>
        ))}
        {!items.length && <div className="empty">Tidak ada layanan yang cocok.</div>}
      </section>
      {total > items.length && (
        <button className="button outline load-more" onClick={loadMore} data-testid="admin-load-more-services">
          Muat lainnya — {nfmt(total - items.length)} layanan lagi
        </button>
      )}
    </>
  );
}

function AdminCategories() {
  const [items, setItems] = useState([]);
  const [nf, setNf] = useState({ name: "", icon: "circle", color: "#20262B" });
  const load = () => api.get("/admin/categories").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const create = async () => { if (!nf.name) return; try { await api.post("/admin/categories", nf); toast.success("Kategori ditambahkan"); setNf({ name: "", icon: "circle", color: "#20262B" }); load(); } catch (x) { toast.error(errorText(x)); } };
  const remove = async (c) => { if (!window.confirm(`Hapus kategori "${c.name}"?`)) return; await api.delete(`/admin/categories/${c.id}`); toast.success("Dihapus"); load(); };
  const clearAll = async () => {
    if (!window.confirm(`Hapus SEMUA ${items.length} kategori? Layanan yang memakainya tetap ada, hanya kategori yang dihapus.`)) return;
    try { const r = await api.post("/admin/categories/clear"); toast.success(`${r.data.deleted} kategori dihapus`); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">ADMIN / CATEGORIES</p><h1>Kategori.</h1><p className="subtle">Kategori muncul otomatis saat import layanan; kamu juga bisa tambah manual.</p></div>
        <div className="header-actions">
          <button className="button outline text-red" onClick={clearAll} disabled={!items.length} data-testid="admin-clear-categories-button"><Trash2 size={15} aria-hidden /> Hapus semua</button>
        </div>
      </header>
      <section className="panel form-panel">
        <div className="two-col">
          <div className="form-section"><label>Nama kategori</label><input value={nf.name} placeholder="Instagram Followers" onChange={(e) => setNf({ ...nf, name: e.target.value })} data-testid="admin-category-name-input" /></div>
          <div className="form-section"><label>Warna aksen</label><input type="color" value={nf.color} onChange={(e) => setNf({ ...nf, color: e.target.value })} /></div>
        </div>
        <button className="button primary" onClick={create} data-testid="admin-category-create-button"><Plus size={15} aria-hidden /> Tambah kategori</button>
      </section>
      <section className="panel table-panel mt-lg">
        <div className="table-head"><span>KATEGORI</span><span>SLUG</span><span>WARNA</span><span>AKSI</span></div>
        {items.map((c) => (
          <div className="table-row" key={c.id} data-testid={`admin-category-${c.id}`}>
            <strong>{c.name}</strong>
            <span className="mono">{c.id}</span>
            <span><span className="swatch" style={{ background: c.color }} /> {c.color}</span>
            <span className="row-actions"><button className="mini-button ghost text-red" onClick={() => remove(c)}>Hapus</button></span>
          </div>
        ))}
      </section>
    </>
  );
}

function AdminPages() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ slug: "", title: "", content: "" });
  const load = () => api.get("/admin/pages").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!nf.slug || !nf.title) { toast.error("Slug dan judul wajib diisi"); return; }
    try { await api.post("/admin/pages", nf); toast.success("Halaman dibuat"); setShowNew(false); setNf({ slug: "", title: "", content: "" }); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const save = async () => {
    if (!edit) return;
    try { await api.put(`/admin/pages/${edit.id}`, { slug: edit.slug, title: edit.title, content: edit.content }); toast.success("Halaman disimpan"); setEdit(null); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Hapus halaman "${p.title}"?`)) return;
    try { await api.delete(`/admin/pages/${p.id}`); toast.success("Halaman dihapus"); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  const pageSlug = (p) => (p.slug === "terms" || p.slug === "privacy" || p.slug === "status") ? "Publik" : "Internal";
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">ADMIN / PAGES</p><h1>Halaman konten.</h1><p className="subtle">Kelola Syarat & Ketentuan, Kebijakan Privasi, dan Status Sistem yang tampil publik.</p></div>
        <button className="button primary" onClick={() => setShowNew(!showNew)} data-testid="admin-new-page-button"><Plus size={15} aria-hidden /> Tambah halaman</button>
      </header>
      {showNew && (
        <section className="panel form-panel mt-lg">
          <p className="eyebrow">HALAMAN BARU</p>
          <div className="two-col">
            <div className="form-section"><label>Slug <small className="hint">huruf kecil, tanpa spasi (cth: kebijakan-refund)</small></label><input value={nf.slug} onChange={(e) => setNf({ ...nf, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} placeholder="kebijakan-refund" data-testid="admin-page-slug-input" /></div>
            <div className="form-section"><label>Judul</label><input value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })} placeholder="Kebijakan Refund" data-testid="admin-page-title-input" /></div>
          </div>
          <div className="form-section"><label>Isi konten</label><textarea rows={8} value={nf.content} onChange={(e) => setNf({ ...nf, content: e.target.value })} placeholder="Tulis konten halaman di sini..." data-testid="admin-page-content-input" /></div>
          <button className="button primary" onClick={create} data-testid="admin-page-create-submit">Simpan halaman</button>
        </section>
      )}
      {edit && (
        <section className="panel form-panel mt-lg">
          <p className="eyebrow">EDIT · {edit.slug}</p>
          <div className="two-col">
            <div className="form-section"><label>Slug</label><input value={edit.slug} disabled className="disabled-input" /></div>
            <div className="form-section"><label>Judul</label><input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="admin-page-edit-title" /></div>
          </div>
          <div className="form-section"><label>Isi konten</label><textarea rows={10} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} data-testid="admin-page-edit-content" /></div>
          <div className="row-actions">
            <button className="button primary" onClick={save} data-testid="admin-page-save-button">Simpan perubahan</button>
            <button className="button outline" onClick={() => setEdit(null)}>Batal</button>
          </div>
        </section>
      )}
      <section className="panel table-panel mt-lg">
        <div className="table-head simple"><span>HALAMAN</span><span>SLUG</span><span>AKSI</span></div>
        {items.map((p) => (
          <div className="table-row simple" key={p.id} data-testid={`admin-page-${p.id}`}>
            <strong>{p.title}<small>Diperbarui {fmtDate(p.updated_at)}</small></strong>
            <span className="mono">/{p.slug} <span className={`status ${pageSlug(p) === "Publik" ? "available" : "warning"}`}>{pageSlug(p)}</span></span>
            <span className="row-actions">
              <a className="mini-button" href={`/${p.slug}`} target="_blank" rel="noreferrer">Lihat</a>
              <button className="mini-button" onClick={() => setEdit({ id: p.id, slug: p.slug, title: p.title, content: p.content })} data-testid={`admin-page-edit-${p.id}`}>Edit</button>
              <button className="mini-button ghost text-red" onClick={() => remove(p)} data-testid={`admin-page-delete-${p.id}`}>Hapus</button>
            </span>
          </div>
        ))}
        {!items.length && <div className="empty">Belum ada halaman. Buat halaman pertama.</div>}
      </section>
    </>
  );
}

function AdminOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setItems(r.data.items || [])); }, []);
  return (
    <>
      <header className="page-head"><div><p className="eyebrow">ADMIN / ORDERS</p><h1>Semua pesanan.</h1></div></header>
      <section className="panel table-panel">
        <div className="table-head"><span>LAYANAN</span><span>USER</span><span className="tr">NILAI / PROFIT</span><span>STATUS</span></div>
        {items.map((o) => (
          <div className="table-row" key={o.id} data-testid={`admin-order-${o.id}`}>
            <strong>{o.service_name}<small>{o.id} · {o.target}</small></strong>
            <span className="mono">{o.user_id?.slice(-6)}</span>
            <div className="tr"><b className="mono">{money(o.price)}</b><small className="mono text-green">+{money(o.profit)}</small></div>
            <span><StatusBadge status={o.status} /></span>
          </div>
        ))}
        {!items.length && <div className="empty">Belum ada pesanan tersimpan.</div>}
      </section>
    </>
  );
}

function AdminDeposits() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/deposits").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const confirm = async (d) => { try { await api.post(`/admin/deposits/${d.id}/mark-paid`); toast.success("Deposit dikonfirmasi & saldo ditambahkan"); load(); } catch (x) { toast.error(errorText(x)); } };
  return (
    <>
      <header className="page-head"><div><p className="eyebrow">ADMIN / DEPOSITS</p><h1>Deposit masuk.</h1><p className="subtle">Klik Konfirmasi manual bila pembayaran offline / transfer sudah masuk rekening.</p></div></header>
      <section className="panel table-panel">
        <div className="table-head"><span>DEPOSIT</span><span>USER</span><span className="tr">NOMINAL</span><span>AKSI</span></div>
        {items.map((d) => (
          <div className="table-row" key={d.id} data-testid={`admin-deposit-${d.id}`}>
            <strong className="mono">{d.id}<small>{fmtDate(d.created_at)} · {d.method} · {d.mode}</small></strong>
            <span>{d.user_email || d.user_id?.slice(-6)}</span>
            <b className="tr mono">{money(d.amount)}</b>
            <span className="row-actions">
              <span className={`status ${d.status === "PAID" ? "available" : d.status === "PENDING" ? "processing" : "danger"}`}>{d.status}</span>
              {d.status === "PENDING" && <button className="mini-button" data-testid={`admin-deposit-confirm-${d.id}`} onClick={() => confirm(d)}>Konfirmasi manual</button>}
            </span>
          </div>
        ))}
        {!items.length && <div className="empty">Belum ada deposit masuk.</div>}
      </section>
    </>
  );
}

function AdminSettings() {
  const [s, setS] = useState(null);
  const [prov, setProv] = useState({ api_url: "https://irvankedesmm.co.id/api", api_id: "", api_key: "", enabled: true });
  const [pay, setPay] = useState({ provider: "midtrans", midtrans_server_key: "", midtrans_client_key: "", midtrans_mode: "sandbox", tripay_api_key: "", tripay_private_key: "", tripay_merchant_code: "", tripay_mode: "sandbox" });
  const [mk, setMk] = useState({ type: "percent", value: 25 });
  const [tiers, setTiers] = useState([]);
  const [land, setLand] = useState(DEFAULT_LANDING);
  const [srv, setSrv] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([
    api.get("/admin/settings").then((r) => {
      setS(r.data);
      setProv({ api_url: r.data.provider?.api_url || "https://irvankedesmm.co.id/api", api_id: r.data.provider?.api_id || "", api_key: r.data.provider?.api_key || "", enabled: r.data.provider?.enabled ?? true });
      setPay((p) => ({ ...p, ...r.data.payment }));
      setMk({ type: r.data.markup?.type || "percent", value: r.data.markup?.value ?? 25 });
      setTiers(r.data.tiers || []);
      setLand({ ...DEFAULT_LANDING, ...(r.data.landing || {}) });
    }),
    api.get("/admin/server-info").then((r) => setSrv(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);
  const saveProv = async () => { setBusy(true); try { await api.put("/admin/settings/provider", prov); toast.success("Provider tersimpan"); load(); } catch (x) { toast.error(errorText(x)); } finally { setBusy(false); } };
  const testProv = async () => { const r = await api.post("/admin/settings/provider/test"); setTestResult(r.data); if (r.data.ok) toast.success(`Terhubung ke ${r.data.provider} · Saldo ${new Intl.NumberFormat("id-ID").format(r.data.balance.balance)}`); else toast.error(r.data.error?.slice(0, 200) || "Gagal test"); };
  const savePay = async () => { setBusy(true); try { await api.put("/admin/settings/payment", pay); toast.success("Payment gateway tersimpan"); load(); } catch (x) { toast.error(errorText(x)); } finally { setBusy(false); } };
  const saveMk = async () => { setBusy(true); try { await api.put("/admin/settings/markup", { type: mk.type, value: Number(mk.value) }); toast.success("Markup tersimpan"); load(); } catch (x) { toast.error(errorText(x)); } finally { setBusy(false); } };
  const saveTiers = async () => { setBusy(true); try { await api.put("/admin/settings/tiers", { tiers: tiers.map((tier) => ({ ...tier, discount: Number(tier.discount) })) }); toast.success("Pengaturan tier tersimpan"); load(); } catch (x) { toast.error(errorText(x)); } finally { setBusy(false); } };
  const saveLand = async () => { setBusy(true); try { await api.put("/admin/settings/landing", land); toast.success("Landing page tersimpan"); load(); } catch (x) { toast.error(errorText(x)); } finally { setBusy(false); } };
  const reprice = async () => { if (!window.confirm("Hitung ulang harga semua layanan berdasarkan markup ini?")) return; const r = await api.post("/admin/provider/reprice"); toast.success(`${r.data.repriced} layanan diperbarui`); };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Disalin ke clipboard"); };
  if (!s) return <div className="loading">Memuat pengaturan...</div>;
  return (
    <>
      <header className="page-head"><div><p className="eyebrow">ADMIN / SETTINGS</p><h1>Pengaturan sistem.</h1><p className="subtle">Konfigurasi provider, payment gateway, markup, dan info server.</p></div></header>
      {srv && (
        <section className="panel server-info mt-lg">
          <p className="eyebrow">SERVER INFO</p>
          <h2>Whitelist ke provider</h2>
          <div className="kv"><span>Public IP server</span><b className="mono">{srv.public_ip || "—"} {srv.public_ip && <button className="mini-button" onClick={() => copy(srv.public_ip)} data-testid="copy-ip-button">Salin</button>}</b></div>
          <div className="kv"><span>Midtrans Notification URL</span><b className="mono truncate">{srv.notification_urls.midtrans} <button className="mini-button" onClick={() => copy(srv.notification_urls.midtrans)}>Salin</button></b></div>
          <div className="kv"><span>Tripay Callback URL</span><b className="mono truncate">{srv.notification_urls.tripay} <button className="mini-button" onClick={() => copy(srv.notification_urls.tripay)}>Salin</button></b></div>
          <div className="summary-note"><ShieldCheck size={16} aria-hidden /> Tambahkan IP di atas ke whitelist provider agar tidak 403. Pasang URL notifikasi di dashboard payment gateway.</div>
        </section>
      )}
      <div className="settings-grid mt-lg">
        <section className="panel">
          <p className="eyebrow">PROVIDER API</p>
          <h2>GuntagoSMM / lainnya</h2>
          <div className="form-section"><label>API URL</label><input value={prov.api_url} onChange={(e) => setProv({ ...prov, api_url: e.target.value })} data-testid="admin-provider-url-input" /></div>
          <div className="form-section"><label>API ID</label><input value={prov.api_id} onChange={(e) => setProv({ ...prov, api_id: e.target.value })} placeholder="9146" data-testid="admin-provider-id-input" /></div>
          <div className="form-section"><label>API Key <small className="hint">Kosongkan/biarkan bertopeng untuk pertahankan key tersimpan</small></label><input value={prov.api_key} onChange={(e) => setProv({ ...prov, api_key: e.target.value })} placeholder="cxuf3h-ekpqtb-..." data-testid="admin-provider-key-input" /></div>
          <div className="form-section"><label>Status</label><select value={prov.enabled ? "1" : "0"} onChange={(e) => setProv({ ...prov, enabled: e.target.value === "1" })}><option value="1">Aktif (LIVE)</option><option value="0">Nonaktif (DEMO)</option></select></div>
          <div className="row-actions">
            <button className="button primary" onClick={saveProv} disabled={busy} data-testid="admin-save-provider-button">Simpan</button>
            <button className="button outline" onClick={testProv} data-testid="admin-test-provider-button">Test koneksi</button>
          </div>
          {testResult && <div className={testResult.ok ? "success" : "error"}>{testResult.ok ? `${testResult.provider} · ${testResult.mode}` : testResult.error?.slice(0, 200)}</div>}
        </section>

        <section className="panel">
          <p className="eyebrow">PAYMENT GATEWAY</p>
          <h2>Midtrans & Tripay</h2>
          <div className="form-section"><label>Provider aktif</label>
            <select value={pay.provider} onChange={(e) => setPay({ ...pay, provider: e.target.value })} data-testid="admin-payment-provider-select"><option value="demo">Demo (manual confirm)</option><option value="midtrans">Midtrans</option><option value="tripay">Tripay</option></select>
          </div>
          <details open={pay.provider === "midtrans"}>
            <summary><b>Midtrans</b> <small>({pay.midtrans_mode})</small></summary>
            <div className="form-section"><label>Server Key</label><input value={pay.midtrans_server_key} onChange={(e) => setPay({ ...pay, midtrans_server_key: e.target.value })} placeholder="SB-Mid-server-..." data-testid="admin-midtrans-server-input" /></div>
            <div className="form-section"><label>Client Key</label><input value={pay.midtrans_client_key} onChange={(e) => setPay({ ...pay, midtrans_client_key: e.target.value })} placeholder="SB-Mid-client-..." /></div>
            <div className="form-section"><label>Mode</label><select value={pay.midtrans_mode} onChange={(e) => setPay({ ...pay, midtrans_mode: e.target.value })}><option value="sandbox">Sandbox</option><option value="production">Production</option></select></div>
          </details>
          <details open={pay.provider === "tripay"}>
            <summary><b>Tripay</b> <small>({pay.tripay_mode})</small></summary>
            <div className="form-section"><label>API Key</label><input value={pay.tripay_api_key} onChange={(e) => setPay({ ...pay, tripay_api_key: e.target.value })} data-testid="admin-tripay-api-input" /></div>
            <div className="form-section"><label>Private Key</label><input value={pay.tripay_private_key} onChange={(e) => setPay({ ...pay, tripay_private_key: e.target.value })} /></div>
            <div className="form-section"><label>Merchant Code</label><input value={pay.tripay_merchant_code} onChange={(e) => setPay({ ...pay, tripay_merchant_code: e.target.value })} placeholder="T1234" /></div>
            <div className="form-section"><label>Mode</label><select value={pay.tripay_mode} onChange={(e) => setPay({ ...pay, tripay_mode: e.target.value })}><option value="sandbox">Sandbox</option><option value="production">Production</option></select></div>
          </details>
          <button className="button primary" onClick={savePay} disabled={busy} data-testid="admin-save-payment-button">Simpan payment gateway</button>
        </section>

        <section className="panel">
          <p className="eyebrow">MARKUP ENGINE</p>
          <h2>Harga jual otomatis</h2>
          <div className="form-section"><label>Tipe markup</label>
            <select value={mk.type} onChange={(e) => setMk({ ...mk, type: e.target.value })} data-testid="admin-markup-type-select"><option value="percent">Persentase (%)</option><option value="fixed">Fixed (Rp)</option></select>
          </div>
          <div className="form-section"><label>Nilai</label><input type="number" step="0.5" value={mk.value} onChange={(e) => setMk({ ...mk, value: e.target.value })} data-testid="admin-markup-value-input" /></div>
          <div className="summary-note">Contoh: modal Rp10.000 dengan markup {mk.value}{mk.type === "percent" ? "%" : " (fixed)"} → harga jual <b className="mono">{money(mk.type === "percent" ? Math.round(10000 * (1 + Number(mk.value) / 100)) : 10000 + Number(mk.value))}</b></div>
          <div className="row-actions">
            <button className="button primary" onClick={saveMk} disabled={busy} data-testid="admin-save-markup-button">Simpan markup</button>
            <button className="button outline" onClick={reprice} data-testid="admin-reprice-button"><RefreshCw size={15} aria-hidden /> Hitung ulang semua harga</button>
          </div>
        </section>

        <section className="panel">
          <p className="eyebrow">TIER PELANGGAN</p>
          <h2>Diskon harga layanan</h2>
          <p className="subtle">Diskon diterapkan otomatis pada katalog dan saat order. Pilih tier untuk tiap akun dari menu Pengguna.</p>
          {tiers.map((tier, index) => (
            <div className="tier-editor" key={tier.id}>
              <div className="form-section"><label>Nama tier</label><input value={tier.name} onChange={(e) => setTiers(tiers.map((item, i) => i === index ? { ...item, name: e.target.value } : item))} data-testid={`admin-tier-name-${tier.id}`} /></div>
              <div className="form-section"><label>Diskon (%)</label><input type="number" min="0" max="100" step="0.5" value={tier.discount} onChange={(e) => setTiers(tiers.map((item, i) => i === index ? { ...item, discount: e.target.value } : item))} data-testid={`admin-tier-discount-${tier.id}`} /></div>
            </div>
          ))}
          <button className="button primary" onClick={saveTiers} disabled={busy} data-testid="admin-save-tiers-button">Simpan tier diskon</button>
        </section>

        <section className="panel">
          <p className="eyebrow">LANDING PAGE</p>
          <h2>Halaman beranda publik</h2>
          <div className="form-section"><label>Status</label>
            <select value={land.enabled ? "1" : "0"} onChange={(e) => setLand({ ...land, enabled: e.target.value === "1" })} data-testid="admin-landing-enabled-select">
              <option value="1">Aktif — tampilkan landing page</option>
              <option value="0">Nonaktif — langsung ke halaman login</option>
            </select>
          </div>
          <div className="form-section"><label>Judul hero</label><input value={land.hero_title} onChange={(e) => setLand({ ...land, hero_title: e.target.value })} data-testid="admin-landing-title-input" /></div>
          <div className="form-section"><label>Subjudul hero</label><input value={land.hero_subtitle} onChange={(e) => setLand({ ...land, hero_subtitle: e.target.value })} data-testid="admin-landing-subtitle-input" /></div>
          <div className="summary-note"><ShieldCheck size={16} aria-hidden /> Matikan bila ingin pengunjung langsung melihat halaman login. Halaman /terms, /privacy, dan /status tetap aktif.</div>
          <button className="button primary" onClick={saveLand} disabled={busy} data-testid="admin-save-landing-button">Simpan landing page</button>
        </section>
      </div>
    </>
  );
}

function AdminTickets() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("Semua");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/tickets").then((r) => setItems(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const open = (t) => { setSel(t); setReply(""); };
  const filtered = items.filter((t) => filter === "Semua" || t.status === filter);
  const counts = { Semua: items.length, open: 0, in_progress: 0, resolved: 0, closed: 0 };
  items.forEach((t) => { if (counts[t.status] !== undefined) counts[t.status] += 1; });
  const sendReply = async () => {
    if (!sel || !reply.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/admin/tickets/${sel.id}/reply`, { message: reply }); toast.success("Balasan terkirim ke customer"); setReply(""); setSel(r.data); load(); }
    catch (x) { toast.error(errorText(x)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    if (!sel) return;
    try { const r = await api.patch(`/admin/tickets/${sel.id}/status`, { status }); toast.success(`Status diubah: ${TICKET_META[status].label}`); setSel({ ...sel, status: r.data.status }); load(); }
    catch (x) { toast.error(errorText(x)); }
  };
  return (
    <>
      <header className="page-head"><div><p className="eyebrow">ADMIN / TICKETS</p><h1>Aduan customer.</h1><p className="subtle">Pantau, balas, dan ubah status aduan. Customer mendapat notifikasi setiap balasan.</p></div></header>
      <div className="filter-bar">
        <div className="pills">
          {["Semua", ...Object.keys(TICKET_META)].map((s) => (
            <button key={s} className={filter === s ? "pill active" : "pill"} onClick={() => setFilter(s)}>{s === "Semua" ? `Semua (${counts.Semua})` : `${TICKET_META[s].label} (${counts[s] || 0})`}</button>
          ))}
        </div>
      </div>
      <section className="panel table-panel">
        <div className="table-head simple"><span>ADUAN</span><span>CUSTOMER</span><span>STATUS</span></div>
        {filtered.map((t) => (
          <div className={`table-row simple ${sel?.id === t.id ? "selected" : ""}`} key={t.id} onClick={() => open(t)} data-testid={`admin-ticket-${t.id}`}>
            <strong>{t.subject}<small>{t.id} · {t.category} · {fmtDate(t.created_at)}</small></strong>
            <span>{t.user_email}</span>
            <span><TicketBadge status={t.status} /></span>
          </div>
        ))}
        {!filtered.length && <div className="empty">Tidak ada aduan dengan status ini.</div>}
      </section>
      {sel && (
        <section className="panel ticket-thread mt-lg">
          <div className="section-head">
            <div><p className="eyebrow">ADUAN {sel.id}</p><h2>{sel.subject}</h2><p className="subtle">{sel.user_email} · {sel.category} · dibuat {fmtDate(sel.created_at)}</p></div>
            <div className="row-actions">
              {Object.keys(TICKET_META).map((s) => (
                <button key={s} className={`mini-button ${sel.status === s ? "active" : ""}`} onClick={() => setStatus(s)} data-testid={`admin-ticket-status-${s}`}>{TICKET_META[s].label}</button>
              ))}
            </div>
          </div>
          <div className={`ticket-msg ${sel.sender === "admin" ? "admin" : ""}`}><b>{sel.user_email} <small className="mono">CUSTOMER · {fmtDate(sel.created_at)}</small></b><p>{sel.message}</p></div>
          {(sel.replies || []).map((r) => (
            <div className={`ticket-msg ${r.sender === "admin" ? "admin" : ""}`} key={r.id}>
              <b>{r.name} <small className="mono">{r.sender === "admin" ? "ADMIN" : "CUSTOMER"} · {fmtDate(r.created_at)}</small></b>
              <p>{r.message}</p>
            </div>
          ))}
          {sel.status !== "closed" && (
            <div className="form-section mt-lg">
              <label>Balas sebagai admin</label>
              <textarea rows={3} placeholder="Tulis balasan..." value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-ticket-reply-input" />
              <button className="button primary mt" onClick={sendReply} disabled={busy || !reply.trim()} data-testid="admin-ticket-reply-button">{busy ? "Mengirim..." : "Kirim balasan"} <ArrowUpRight size={16} aria-hidden /></button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

// -------------------- PROFILE --------------------
function Profile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    if (form.new_password.length < 6) { setMsg("Password baru minimal 6 karakter."); return; }
    if (form.new_password !== form.confirm) { setMsg("Konfirmasi password tidak sama."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: form.old_password, new_password: form.new_password });
      toast.success("Password berhasil diubah");
      setForm({ old_password: "", new_password: "", confirm: "" });
    } catch (x) { setMsg(errorText(x)); } finally { setBusy(false); }
  };
  return (
    <>
      <header className="page-head">
        <div><p className="eyebrow">PROFILE</p><h1>Akun kamu.</h1><p className="subtle">Kelola informasi akun dan ganti kata sandi.</p></div>
      </header>
      <div className="order-layout">
        <section className="panel form-panel">
          <p className="eyebrow">INFORMASI AKUN</p>
          <div className="kv"><span>Nama</span><b>{me?.name}</b></div>
          <div className="kv"><span>Email</span><b>{me?.email}</b></div>
          <div className="kv"><span>Peran</span><b>{me?.role === "admin" ? "Administrator" : "Member"}</b></div>
        </section>
        <form className="panel form-panel" onSubmit={submit}>
          <p className="eyebrow">GANTI KATA SANDI</p>
          <div className="form-section">
            <label>Password lama</label>
            <input data-testid="profile-old-password" type="password" autoComplete="current-password" required value={form.old_password} onChange={(e) => setForm({ ...form, old_password: e.target.value })} />
          </div>
          <div className="form-section">
            <label>Password baru</label>
            <input data-testid="profile-new-password" type="password" autoComplete="new-password" minLength={6} required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
          </div>
          <div className="form-section">
            <label>Ulangi password baru</label>
            <input data-testid="profile-confirm-password" type="password" autoComplete="new-password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
          </div>
          {msg && <div className="error">{msg}</div>}
          <button data-testid="profile-change-password-button" className="button primary wide" type="submit" disabled={busy}>
            {busy ? "Menyimpan..." : "Ganti kata sandi"}
          </button>
        </form>
      </div>
    </>
  );
}

// -------------------- APP ROOT --------------------
function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [unread, setUnread] = useState(0);
  const [pub, setPub] = useState({ landing: DEFAULT_LANDING, landing_enabled: true });
  const refreshUnread = useCallback(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setUnread((r.data.items || []).filter((n) => !n.read).length)).catch(() => {});
  }, [user]);
  useEffect(() => {
    api.get("/public/config").then((r) => setPub(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}).finally(() => setChecking(false));
  }, []);
  useEffect(() => { refreshUnread(); const t = setInterval(refreshUnread, 20000); return () => clearInterval(t); }, [refreshUnread]);
  if (checking) return <div className="loading">Membuka Guntago...</div>;
  if (!user) return (
    <>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/" element={pub.landing_enabled ? <Landing pub={pub} /> : <Auth onLogin={setUser} landingEnabled={false} />} />
        <Route path="/auth" element={<Auth onLogin={setUser} landingEnabled={pub.landing_enabled} />} />
        <Route path="/terms" element={<ContentPage slug="terms" />} />
        <Route path="/privacy" element={<ContentPage slug="privacy" />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="*" element={<Navigate to={pub.landing_enabled ? "/" : "/auth"} />} />
      </Routes>
    </>
  );
  const logout = async () => { await api.post("/auth/logout"); setUser(null); };
  return (
    <>
      <Toaster position="bottom-right" richColors offset="20px" />
      <Shell user={user} onLogout={logout} unread={unread}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={<Dashboard user={user} />} />
          <Route path="/services" element={<Services />} />
          <Route path="/tatacara-pemesanan" element={<OrderingGuide />} />
          <Route path="/order/:id" element={<OrderRoute />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/refills" element={<Refills />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/notifications" element={<Notifications refresh={refreshUnread} />} />
          <Route path="/profile" element={<Profile />} />
          {user.role === "admin" && <Route path="/admin" element={<AdminOverview />} />}
          {user.role === "admin" && <Route path="/admin/users" element={<AdminUsers />} />}
          {user.role === "admin" && <Route path="/admin/services" element={<AdminServices />} />}
          {user.role === "admin" && <Route path="/admin/categories" element={<AdminCategories />} />}
          {user.role === "admin" && <Route path="/admin/pages" element={<AdminPages />} />}
          {user.role === "admin" && <Route path="/admin/orders" element={<AdminOrders />} />}
          {user.role === "admin" && <Route path="/admin/tickets" element={<AdminTickets />} />}
          {user.role === "admin" && <Route path="/admin/deposits" element={<AdminDeposits />} />}
          {user.role === "admin" && <Route path="/admin/settings" element={<AdminSettings />} />}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Shell>
    </>
  );
}
function OrderRoute() { const { id } = useParams(); return <OrderPage id={id} />; }
export default function Root() { return (<BrowserRouter><App /></BrowserRouter>); }
